import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { ErrorBanner } from "../../components/admin/AdminUI";

const schema = z
  .object({
    wachtwoord: z.string().min(12, "Minimaal 12 tekens"),
    herhaling: z.string().min(1, "Herhaal het wachtwoord"),
  })
  .refine((v) => v.wachtwoord === v.herhaling, {
    path: ["herhaling"],
    message: "De twee wachtwoorden zijn niet gelijk",
  });

/**
 * Stap 2 van "wachtwoord vergeten": een nieuw wachtwoord kiezen.
 *
 * Supabase hangt het herstel-token achter een hekje in het adres
 * (#access_token=…&type=recovery). Dat deel gaat nooit naar een server mee, dus het
 * moet hier uit de balk gelezen worden. Meteen daarna halen we het uit de adresbalk
 * weg, zodat het niet in de geschiedenis van de browser achterblijft.
 */
function leesTokensUitAdres() {
  if (typeof window === "undefined") return {};
  const ruw = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.search.replace(/^\?/, "");
  const p = new URLSearchParams(ruw);
  return {
    accessToken: p.get("access_token"),
    refreshToken: p.get("refresh_token"),
    soort: p.get("type"),
    foutOmschrijving: p.get("error_description") || p.get("error"),
  };
}

export default function WachtwoordHerstellenPage() {
  const navigate = useNavigate();
  const tokens = useMemo(leesTokensUitAdres, []);
  const [apiError, setApiError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [gelukt, setGelukt] = useState(false);

  useEffect(() => {
    if (tokens.accessToken && typeof window !== "undefined") {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [tokens.accessToken]);

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { wachtwoord: "", herhaling: "" },
  });

  async function onSubmit(values) {
    setApiError(null);
    setSubmitting(true);
    try {
      await api.post("/auth/admin/wachtwoord-herstellen", {
        access_token: tokens.accessToken,
        refresh_token: tokens.refreshToken,
        nieuw_wachtwoord: values.wachtwoord,
      });
      setGelukt(true);
      setTimeout(() => navigate("/admin/login", { replace: true }), 2500);
    } catch (e) {
      setApiError(e);
    } finally {
      setSubmitting(false);
    }
  }

  const geenToken = !tokens.accessToken;

  return (
    <div
      className="fulkra flex min-h-screen items-center justify-center px-4 py-12"
      style={{ background: "var(--surface-page)" }}
      data-testid="wachtwoord-herstellen-page"
    >
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Fulkra Works</h1>
          <p className="text-sm text-slate-600" style={{ marginTop: "var(--ruimte-4)" }}>Preventieprogramma voor staand en lopend werk</p>
        </div>
        <div
          style={{
            borderRadius: "var(--radius-vlak)", border: "1px solid var(--border-subtle)",
            background: "var(--surface-card)", padding: "var(--ruimte-9)",
            boxShadow: "var(--elevation-card)",
          }}
        >
          {gelukt ? (
            <div data-testid="herstellen-gelukt">
              <h2 className="text-xl font-semibold text-slate-900">Uw wachtwoord is gewijzigd</h2>
              <p className="text-sm text-slate-600" style={{ marginTop: "var(--ruimte-5)" }}>
                U wordt naar het inlogscherm gebracht. Gebruik voortaan uw nieuwe wachtwoord.
              </p>
            </div>
          ) : geenToken ? (
            <div data-testid="herstellen-geen-token">
              <h2 className="text-xl font-semibold text-slate-900">Deze link werkt niet</h2>
              <p className="text-sm text-slate-600" style={{ marginTop: "var(--ruimte-5)" }}>
                {tokens.foutOmschrijving
                  ? `De link gaf deze melding terug: ${tokens.foutOmschrijving}.`
                  : "Er staat geen geldig hersteltoken in de link."}{" "}
                Een herstellink is één keer te gebruiken en verloopt na een uur.
              </p>
              <Link
                to="/admin/wachtwoord-vergeten"
                className="mt-6 inline-flex text-sm text-blue-700 underline underline-offset-4 hover:text-blue-800"
              >
                Vraag een nieuwe link aan
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-slate-900">Kies een nieuw wachtwoord</h2>
              <p className="text-sm text-slate-500" style={{ marginTop: "var(--ruimte-3)" }}>Minimaal 12 tekens.</p>

              <form className="mt-6 flex flex-col gap-5" onSubmit={handleSubmit(onSubmit)} noValidate>
                <ErrorBanner error={apiError} />
                <label className="block text-sm">
                  <span className="font-medium text-slate-700">Nieuw wachtwoord</span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    data-testid="herstellen-wachtwoord"
                    {...register("wachtwoord")}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                  {errors.wachtwoord ? (
                    <p className="text-xs text-rose-600" data-testid="herstellen-wachtwoord-error" style={{ marginTop: "var(--ruimte-3)" }}>
                      {errors.wachtwoord.message}
                    </p>
                  ) : null}
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-slate-700">Herhaal het wachtwoord</span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    data-testid="herstellen-herhaling"
                    {...register("herhaling")}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 transition focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                  {errors.herhaling ? (
                    <p className="text-xs text-rose-600" data-testid="herstellen-herhaling-error" style={{ marginTop: "var(--ruimte-3)" }}>
                      {errors.herhaling.message}
                    </p>
                  ) : null}
                </label>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="btn-herstellen-submit"
                  className="mt-2 inline-flex justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 active:bg-blue-800 disabled:opacity-60"
                >
                  {submitting ? "Bezig…" : "Wachtwoord opslaan"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
